import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import {
  EINRICHTUNGSTYPEN,
  BUNDESLAENDER,
  QUALIFIKATIONEN,
  ARBEITSBEREICHE,
  ARBEITSMODELLE,
  SPRACHLEVEL,
  BENEFITS_OPTIONEN,
  ZUSCHLAEGE_STUFEN,
  EINSPRINGEN_HAEUFIGKEIT,
  WOCHENEND_FREQUENZ,
  FEEDBACK_RHYTHMUS,
} from '../../lib/constants'
import SliderProfile from './SliderProfile'
import './AdminPages.css'

interface AGData {
  id: string
  name: string
  created_at: string
  einrichtungstyp: string | null
  plz: string | null
  ort: string | null
  bundesland: string | null
  herkunft: string | null
  website: string | null
  interne_notizen: string | null
  status: string
  offen_fuer_direktansprache: boolean | null

  // Bedarf
  wunschqualifikationen: string[] | null
  einsatzbereiche: string[] | null
  arbeitsmodelle: string[] | null
  wochenstunden_min: number | null
  wochenstunden_max: number | null
  mindestberufserfahrung: number | null
  anerkennung_erforderlich: string | null
  sprachlevel: string | null

  // Vergütung
  gehaltsrahmen_min: number | null
  gehaltsrahmen_max: number | null
  zuschlaege: string | null
  benefits: string[] | null

  // Schichten
  nachtdienst_erforderlich: string | null
  wochenenddienst_erforderlich: string | null
  geteilte_dienste: string | null
  rufbereitschaft: string | null

  // Profil
  slider_antworten: Record<string, number> | null
  fakten: Record<string, unknown> | null
  besonderheiten: string | null

  // Onboarding
  onboarding_dauer_wochen: number | null
  feste_ansprechperson: boolean | null
  hospitation_moeglich: boolean | null
  umzugshilfe: string | null
  anerkennungshilfe: string | null
}

interface Standort {
  id: string
  name: string | null
  ort: string | null
  plz: string | null
  bundesland: string | null
}

interface Ansprechpartner {
  id: string
  name: string
  position: string | null
  email: string | null
  telefon: string | null
  ist_hauptkontakt: boolean
}

interface MatchRow {
  id: string
  score: number
  kategorie: string
  pflegekraefte: { id: string; vorname: string; nachname: string; qualifikation: string | null; bundesland: string | null }
}

export default function ArbeitgeberDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [ag, setAg] = useState<AGData | null>(null)
  const [standorte, setStandorte] = useState<Standort[]>([])
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([])
  const [matches, setMatches] = useState<MatchRow[]>([])
  const [loading, setLoading] = useState(true)
  const [notizen, setNotizen] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (id) loadData()
  }, [id])

  async function loadData() {
    setLoading(true)
    const [agRes, stRes, kRes, mRes] = await Promise.all([
      supabase.from('arbeitgeber').select('*').eq('id', id!).single(),
      supabase.from('arbeitgeber_standorte').select('*').eq('arbeitgeber_id', id!),
      supabase.from('arbeitgeber_ansprechpartner').select('*').eq('arbeitgeber_id', id!).order('ist_hauptkontakt', { ascending: false }),
      supabase
        .from('matches')
        .select('id, score, kategorie, pflegekraefte!inner(id, vorname, nachname, qualifikation, bundesland)')
        .eq('arbeitgeber_id', id!)
        .order('score', { ascending: false })
        .limit(30),
    ])
    if (agRes.data) {
      setAg(agRes.data as AGData)
      setNotizen(agRes.data.interne_notizen || '')
    }
    setStandorte((stRes.data as Standort[]) || [])
    setKontakte((kRes.data as Ansprechpartner[]) || [])
    setMatches((mRes.data as unknown as MatchRow[]) || [])
    setLoading(false)
  }

  async function saveNotizen() {
    setSaving(true)
    await supabase.from('arbeitgeber').update({ interne_notizen: notizen }).eq('id', id!)
    setSaving(false)
  }

  const label = (list: readonly { value: string; label: string }[], val: string | null | undefined) =>
    list.find((i) => i.value === val)?.label || val || '–'
  const haeuf = (val: string | null | undefined) =>
    val === 'ja' ? '✅ Ja, regelmäßig' : val === 'selten' ? '🟡 Selten' : val === 'nein' ? '❌ Nein' : '–'
  const jaNein = (val: unknown) => (val === true ? '✅ Ja' : val === false ? '❌ Nein' : '–')
  const arrayLabel = (list: readonly { value: string; label: string }[], arr: string[] | null) =>
    arr && arr.length > 0 ? arr.map((v) => label(list, v)).join(', ') : '–'

  if (loading) return <div className="table-loading"><div className="loading-spinner" /></div>
  if (!ag) return <div className="admin-page"><p>Einrichtung nicht gefunden.</p></div>

  const fakten = (ag.fakten || {}) as Record<string, unknown>

  return (
    <div className="admin-page animate-fade-in-up">
      <button className="back-button" onClick={() => navigate('/admin/arbeitgeber')}>← Zurück zur Liste</button>

      <div className="detail-header">
        <div className="detail-title-row">
          <h1>🏢 {ag.name}</h1>
          <span
            className="status-badge status-badge--lg"
            style={{ '--badge-color': ag.status === 'aktiv' ? '#10B981' : ag.status === 'neu' ? '#3B82F6' : '#94A3B8' } as React.CSSProperties}
          >
            {ag.status}
          </span>
        </div>
        <p className="page-subtitle">
          {label(EINRICHTUNGSTYPEN, ag.einrichtungstyp)}
          {(ag.plz || ag.ort) && <> · {ag.plz} {ag.ort}</>}
          {ag.bundesland && <> · {label(BUNDESLAENDER, ag.bundesland)}</>}
        </p>
      </div>

      <div className="detail-grid">
        {/* Grunddaten */}
        <div className="detail-section">
          <h3>🏥 Grunddaten</h3>
          <dl className="detail-dl">
            <dt>Einrichtungstyp</dt><dd>{label(EINRICHTUNGSTYPEN, ag.einrichtungstyp)}</dd>
            <dt>Standort</dt><dd>{ag.plz || ag.ort ? `${ag.plz || ''} ${ag.ort || ''}`.trim() : '–'}</dd>
            <dt>Bundesland</dt><dd>{label(BUNDESLAENDER, ag.bundesland)}</dd>
            <dt>Erfasst über</dt><dd>{ag.herkunft === 'funnel' ? 'Arbeitgeber-Formular' : 'Admin'}</dd>
            <dt>Website</dt><dd>{ag.website ? <a href={ag.website} target="_blank" rel="noopener">{ag.website}</a> : '–'}</dd>
            <dt>Direktansprache</dt><dd>{ag.offen_fuer_direktansprache ? '✅ Offen' : '❌ Nicht gewünscht'}</dd>
          </dl>
        </div>

        {/* Bedarf */}
        <div className="detail-section">
          <h3>🎯 Bedarf an Pflegekräften</h3>
          <dl className="detail-dl">
            <dt>Qualifikationen</dt><dd>{arrayLabel(QUALIFIKATIONEN, ag.wunschqualifikationen)}</dd>
            <dt>Einsatzbereiche</dt><dd>{arrayLabel(ARBEITSBEREICHE, ag.einsatzbereiche)}</dd>
            <dt>Arbeitsmodelle</dt><dd>{arrayLabel(ARBEITSMODELLE, ag.arbeitsmodelle)}</dd>
            <dt>Wochenstunden</dt>
            <dd>{ag.wochenstunden_min || ag.wochenstunden_max ? `${ag.wochenstunden_min ?? '?'} – ${ag.wochenstunden_max ?? '?'} Std.` : '–'}</dd>
            <dt>Mind. Erfahrung</dt><dd>{ag.mindestberufserfahrung != null ? `${ag.mindestberufserfahrung} Jahre` : '–'}</dd>
            <dt>Anerkennung</dt><dd>{haeuf(ag.anerkennung_erforderlich)}</dd>
            <dt>Sprachlevel</dt><dd>{label(SPRACHLEVEL, ag.sprachlevel)}</dd>
          </dl>
        </div>

        {/* Vergütung */}
        <div className="detail-section">
          <h3>💰 Vergütung & Benefits</h3>
          <dl className="detail-dl">
            <dt>Gehaltsrahmen</dt>
            <dd>{ag.gehaltsrahmen_min || ag.gehaltsrahmen_max
              ? `${ag.gehaltsrahmen_min?.toLocaleString('de-DE') || '?'} – ${ag.gehaltsrahmen_max?.toLocaleString('de-DE') || '?'} €`
              : '–'}</dd>
            <dt>Zuschläge</dt><dd>{label(ZUSCHLAEGE_STUFEN, ag.zuschlaege)}</dd>
          </dl>
          {ag.benefits && ag.benefits.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <strong style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>Benefits</strong>
              <div className="chip-list" style={{ marginTop: 'var(--space-2)' }}>
                {ag.benefits.map((b, i) => (
                  <span key={i} className="chip chip--teal">{label(BENEFITS_OPTIONEN, b)}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Schicht-Anforderungen */}
        <div className="detail-section">
          <h3>🌙 Schicht-Anforderungen</h3>
          <dl className="detail-dl">
            <dt>Nachtdienst</dt><dd>{haeuf(ag.nachtdienst_erforderlich)}</dd>
            <dt>Wochenende</dt><dd>{haeuf(ag.wochenenddienst_erforderlich)}</dd>
            <dt>Geteilte Dienste</dt><dd>{haeuf(ag.geteilte_dienste)}</dd>
            <dt>Rufbereitschaft</dt><dd>{haeuf(ag.rufbereitschaft)}</dd>
          </dl>
        </div>
      </div>

      {/* Faktenfelder */}
      {Object.keys(fakten).length > 0 && (
        <div className="detail-section">
          <h3>🔍 Faktencheck (Plausibilisierung)</h3>
          <dl className="detail-dl">
            {fakten.dienstplan_vorlauf_wochen != null && <><dt>Dienstplan-Vorlauf</dt><dd>{String(fakten.dienstplan_vorlauf_wochen)} Wochen</dd></>}
            {fakten.ueberstunden_pro_monat != null && <><dt>Überstunden/Monat</dt><dd>{String(fakten.ueberstunden_pro_monat)} Std.</dd></>}
            {fakten.einspringen_haeufigkeit != null && <><dt>Einspringen</dt><dd>{label(EINSPRINGEN_HAEUFIGKEIT, fakten.einspringen_haeufigkeit as string)}</dd></>}
            {fakten.wochenend_frequenz != null && <><dt>Wochenend-Frequenz</dt><dd>{label(WOCHENEND_FREQUENZ, fakten.wochenend_frequenz as string)}</dd></>}
            {fakten.betreuungsschluessel != null && <><dt>Betreuungsschlüssel</dt><dd>{String(fakten.betreuungsschluessel)}</dd></>}
            {'digitale_doku' in fakten && <><dt>Digitale Doku</dt><dd>{jaNein(fakten.digitale_doku)}</dd></>}
            {'hilfskraefte_vorhanden' in fakten && <><dt>Hilfskräfte</dt><dd>{jaNein(fakten.hilfskraefte_vorhanden)}</dd></>}
            {'hebehilfen_vorhanden' in fakten && <><dt>Hebehilfen</dt><dd>{jaNein(fakten.hebehilfen_vorhanden)}</dd></>}
            {fakten.feedback_rhythmus != null && <><dt>Feedbackgespräche</dt><dd>{label(FEEDBACK_RHYTHMUS, fakten.feedback_rhythmus as string)}</dd></>}
            {'praxisanleitung' in fakten && <><dt>Praxisanleitung</dt><dd>{jaNein(fakten.praxisanleitung)}</dd></>}
          </dl>
        </div>
      )}

      {/* Slider-Profil */}
      <div className="detail-section">
        <h3>⭐ Arbeitsbedingungen-Profil</h3>
        <SliderProfile answers={ag.slider_antworten} side="employer" />
      </div>

      {/* Onboarding */}
      <div className="detail-section">
        <h3>🧭 Onboarding & Wechselbegleitung</h3>
        <dl className="detail-dl">
          <dt>Einarbeitung</dt><dd>{ag.onboarding_dauer_wochen != null ? `${ag.onboarding_dauer_wochen} Wochen` : '–'}</dd>
          <dt>Feste Ansprechperson</dt><dd>{jaNein(ag.feste_ansprechperson)}</dd>
          <dt>Hospitation</dt><dd>{jaNein(ag.hospitation_moeglich)}</dd>
          <dt>Umzugshilfe</dt><dd>{ag.umzugshilfe || '–'}</dd>
          <dt>Anerkennungshilfe</dt><dd>{ag.anerkennungshilfe || '–'}</dd>
        </dl>
      </div>

      {/* Besonderheiten */}
      {ag.besonderheiten && (
        <div className="detail-section">
          <h3>✨ Besonderheiten</h3>
          <div className="detail-freetext"><p style={{ whiteSpace: 'pre-wrap' }}>{ag.besonderheiten}</p></div>
        </div>
      )}

      {/* Ansprechpartner */}
      <div className="detail-section">
        <h3>👤 Ansprechpartner ({kontakte.length})</h3>
        {kontakte.length > 0 ? (
          <div className="top-matches-list">
            {kontakte.map((k) => (
              <div key={k.id} className="top-match-item" style={{ cursor: 'default' }}>
                <div className="top-match-score" style={{ color: k.ist_hauptkontakt ? 'var(--color-primary)' : 'var(--color-gray-400)', fontSize: 'var(--text-lg)' }}>
                  {k.ist_hauptkontakt ? '⭐' : '👤'}
                </div>
                <div className="top-match-info">
                  <span className="top-match-name">{k.name} {k.position && `(${k.position})`}</span>
                  <span className="top-match-stelle">{k.email && <>{k.email} · </>}{k.telefon || ''}</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="empty-hint" style={{ textAlign: 'center', padding: 'var(--space-4)' }}>Keine Ansprechpartner hinterlegt.</p>}
      </div>

      {/* Standorte (falls vorhanden) */}
      {standorte.length > 0 && (
        <div className="detail-section">
          <h3>📍 Weitere Standorte ({standorte.length})</h3>
          <div className="top-matches-list">
            {standorte.map((s) => (
              <div key={s.id} className="top-match-item" style={{ cursor: 'default' }}>
                <div className="top-match-score" style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-lg)' }}>📍</div>
                <div className="top-match-info">
                  <span className="top-match-name">{s.name || s.ort || 'Standort'}</span>
                  <span className="top-match-stelle">{s.plz} {s.ort} · {label(BUNDESLAENDER, s.bundesland)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matches */}
      <div className="detail-section">
        <h3>🎯 Top-Matches ({matches.length})</h3>
        {matches.length > 0 ? (
          <div className="top-matches-list">
            {matches.map((m) => (
              <div key={m.id} className="top-match-item" onClick={() => navigate(`/admin/pflegekraefte/${m.pflegekraefte.id}`)}>
                <div className="top-match-score" style={{ color: m.score >= 80 ? '#10B981' : m.score >= 60 ? '#3B82F6' : '#F59E0B' }}>
                  {m.score}
                </div>
                <div className="top-match-info">
                  <span className="top-match-name">👤 {m.pflegekraefte.vorname} {m.pflegekraefte.nachname}</span>
                  <span className="top-match-stelle">
                    {label(QUALIFIKATIONEN, m.pflegekraefte.qualifikation)} · {label(BUNDESLAENDER, m.pflegekraefte.bundesland)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-hint" style={{ textAlign: 'center', padding: 'var(--space-4)' }}>
            Noch keine Matches berechnet. Starten Sie das Matching über die Matching-Seite.
          </p>
        )}
      </div>

      {/* Notizen */}
      <div className="detail-section">
        <h3>🗒️ Interne Notizen</h3>
        <textarea className="notes-textarea" value={notizen} onChange={(e) => setNotizen(e.target.value)} rows={4} placeholder="Interne Notizen..." />
        <button className="btn btn--secondary" onClick={saveNotizen} disabled={saving}>
          {saving ? 'Speichern...' : '💾 Notizen speichern'}
        </button>
      </div>
    </div>
  )
}
